function initDelegatedTooltips() {
    // Tooltips are initialized globally because many views reuse HTML partials (including AJAX fragments).
    if (window.__delegatedTooltipsV1Initialized) {
        return;
    }

    window.__delegatedTooltipsV1Initialized = true;

    // We use generic attributes so any future tooltip can opt-in without extra JS wiring.
    const triggerSelector = '.js-tooltip-trigger[data-tooltip-content]';
    const supportsPopperV2 = Boolean(window.Popper && typeof window.Popper.createPopper === 'function');

    let activeTooltip = null;

    /**
     * @param {EventTarget|null} target
     * @return {Element|null}
     */
    const getTriggerElement = (target) => {
        if (!(target instanceof Element)) {
            return null;
        }

        return target.closest(triggerSelector);
    };

    // Keep browser-native title as a fallback for contexts without JS (emails/legacy views).
    const removeNativeTitle = (trigger) => {
        if (!trigger.hasAttribute('title')) {
            return;
        }

        trigger.dataset.nativeTitle = trigger.getAttribute('title') || '';
        trigger.removeAttribute('title');
    };

    const restoreNativeTitle = (trigger) => {
        if (!Object.prototype.hasOwnProperty.call(trigger.dataset, 'nativeTitle')) {
            return;
        }

        trigger.setAttribute('title', trigger.dataset.nativeTitle || '');
        delete trigger.dataset.nativeTitle;
    };

    // Fallback positioning for pages where Popper v2 is not available.
    const positionTooltipFallback = (tooltip) => {
        const {element, trigger} = tooltip;
        const margin = 8;
        const offset = 10;

        element.style.position = 'fixed';

        const triggerRect = trigger.getBoundingClientRect();
        const tooltipRect = element.getBoundingClientRect();

        const centeredLeft = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        const maxLeft = window.innerWidth - tooltipRect.width - margin;
        const left = Math.max(margin, Math.min(centeredLeft, maxLeft));

        let top = triggerRect.top - tooltipRect.height - offset;
        if (top < margin) {
            top = triggerRect.bottom + offset;
        }

        element.style.left = `${left}px`;
        element.style.top = `${top}px`;
    };

    const syncActiveTooltipPosition = () => {
        if (!activeTooltip) {
            return;
        }

        if (activeTooltip.popperInstance) {
            void activeTooltip.popperInstance.update();
            return;
        }

        positionTooltipFallback(activeTooltip);
    };

    const destroyActiveTooltip = () => {
        if (!activeTooltip) {
            return;
        }

        restoreNativeTitle(activeTooltip.trigger);

        if (activeTooltip.popperInstance) {
            activeTooltip.popperInstance.destroy();
        }

        if (activeTooltip.element && activeTooltip.element.parentElement) {
            activeTooltip.element.parentElement.removeChild(activeTooltip.element);
        }

        activeTooltip = null;
    };

    const showTooltip = (trigger, pinned) => {
        const description = (trigger.dataset.tooltipContent || '').trim();

        if (!description) {
            return;
        }

        if (activeTooltip && activeTooltip.trigger === trigger) {
            activeTooltip.pinned = pinned;
            activeTooltip.element.classList.remove('d-none');
            syncActiveTooltipPosition();
            return;
        }

        destroyActiveTooltip();
        removeNativeTitle(trigger);

        const tooltipElement = document.createElement('div');
        tooltipElement.className = 'alert alert-info py-2 px-2 mb-0';
        tooltipElement.style.maxWidth = '260px';
        tooltipElement.style.zIndex = '1080';
        tooltipElement.style.lineHeight = '1.2';
        tooltipElement.textContent = description;

        document.body.appendChild(tooltipElement);

        let popperInstance = null;

        if (supportsPopperV2) {
            popperInstance = window.Popper.createPopper(trigger, tooltipElement, {
                placement: 'top',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 10],
                        },
                    },
                ],
            });
        }

        activeTooltip = {
            trigger,
            element: tooltipElement,
            popperInstance,
            pinned,
        };

        syncActiveTooltipPosition();
    };

    // Delegated handlers make this work for both static pages and AJAX-replaced preview fragments.
    document.addEventListener('mouseover', (event) => {
        const trigger = getTriggerElement(event.target);

        if (!trigger) {
            return;
        }

        showTooltip(trigger, false);
    });

    document.addEventListener('mouseout', (event) => {
        const trigger = getTriggerElement(event.target);

        if (!trigger || !activeTooltip || activeTooltip.trigger !== trigger || activeTooltip.pinned) {
            return;
        }

        const relatedTarget = event.relatedTarget;
        if (relatedTarget instanceof Element && trigger.contains(relatedTarget)) {
            return;
        }

        destroyActiveTooltip();
    });

    document.addEventListener('focusin', (event) => {
        const trigger = getTriggerElement(event.target);

        if (!trigger) {
            return;
        }

        showTooltip(trigger, false);
    });

    document.addEventListener('focusout', (event) => {
        const trigger = getTriggerElement(event.target);

        if (!trigger || !activeTooltip || activeTooltip.trigger !== trigger || activeTooltip.pinned) {
            return;
        }

        destroyActiveTooltip();
    });

    document.addEventListener('click', (event) => {
        const trigger = getTriggerElement(event.target);

        if (trigger) {
            event.preventDefault();
            event.stopPropagation();

            if (activeTooltip && activeTooltip.trigger === trigger && activeTooltip.pinned) {
                destroyActiveTooltip();
                return;
            }

            showTooltip(trigger, true);
            return;
        }

        if (
            activeTooltip &&
            event.target instanceof Element &&
            !activeTooltip.element.contains(event.target) &&
            !activeTooltip.trigger.contains(event.target)
        ) {
            destroyActiveTooltip();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            destroyActiveTooltip();
        }
    });

    // Keep open tooltip attached to trigger while viewport/layout changes.
    window.addEventListener('resize', () => {
        syncActiveTooltipPosition();
    });

    window.addEventListener('scroll', () => {
        syncActiveTooltipPosition();
    }, true);
}

export {initDelegatedTooltips};
